import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send,
  MessageSquare,
  Users,
  Bell,
  Search,
  Filter,
  Plus,
  CheckCircle,
  Clock,
  Star,
  Reply,
  Forward,
  Paperclip
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

interface Message {
  id: string;
  fromId: string;
  fromName: string;
  fromAvatar?: string;
  fromRole: 'coach' | 'parent' | 'player' | 'admin';
  toId: string;
  toName: string;
  subject: string;
  content: string;
  timestamp: string;
  status: 'sent' | 'delivered' | 'read' | 'replied';
  priority: 'low' | 'normal' | 'high';
  category: 'feedback' | 'progress' | 'scheduling' | 'general' | 'urgent';
  attachments?: string[];
  isRead: boolean;
}

interface Conversation {
  id: string;
  participants: {
    id: string;
    name: string;
    avatar?: string;
    role: string;
  }[];
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  category: string;
}

const mockMessages: Message[] = [
  {
    id: '1',
    fromId: 'coach1',
    fromName: 'Coach Sarah Wilson',
    fromRole: 'coach',
    toId: 'parent1',
    toName: 'Michael Thompson (Parent)',
    subject: 'Alex\'s Weekly Progress Update',
    content: 'Hi Michael, I wanted to share Alex\'s progress from this week. He\'s shown significant improvement in his timing and has increased his average shot distance by 15%. His consistency score has also improved from 72% to 78%. I recommend focusing on his footwork in the coming sessions. Please let me know if you have any questions!',
    timestamp: '2024-01-15T14:30:00Z',
    status: 'delivered',
    priority: 'normal',
    category: 'progress',
    isRead: false
  },
  {
    id: '2',
    fromId: 'parent2',
    fromName: 'Lisa Chen (Parent)',
    fromRole: 'parent',
    toId: 'coach1',
    toName: 'Coach Sarah Wilson',
    subject: 'Question about Sarah\'s training schedule',
    content: 'Hi Coach Wilson, I hope you\'re doing well. I wanted to ask about adjusting Sarah\'s training schedule next week due to her school exams. Would it be possible to reschedule her Tuesday session to Thursday? Also, could you provide some practice drills she can do at home during this period?',
    timestamp: '2024-01-15T10:15:00Z',
    status: 'read',
    priority: 'normal',
    category: 'scheduling',
    isRead: true
  },
  {
    id: '3',
    fromId: 'admin1',
    fromName: 'Academy Admin',
    fromRole: 'admin',
    toId: 'all_coaches',
    toName: 'All Coaches',
    subject: 'New Equipment Arrival - Updated Guidelines',
    content: 'Dear Coaches, We\'ve received the new batting equipment including the latest smart bats with sensors. Please review the updated safety guidelines and usage instructions in the attached document. Training session on the new equipment will be held this Friday at 3 PM.',
    timestamp: '2024-01-15T09:00:00Z',
    status: 'delivered',
    priority: 'high',
    category: 'general',
    attachments: ['equipment_guidelines.pdf'],
    isRead: false
  },
  {
    id: '4',
    fromId: 'coach2',
    fromName: 'Coach Mike Johnson',
    fromRole: 'coach',
    toId: 'coach1',
    toName: 'Coach Sarah Wilson',
    subject: 'Player assessment collaboration',
    content: 'Sarah, I\'ve been working with Emma on her batting technique and noticed some interesting patterns in her performance data. Would you like to collaborate on a comprehensive assessment? I think your expertise with footwork analysis would be valuable. Let me know when you\'re available to discuss.',
    timestamp: '2024-01-14T16:45:00Z',
    status: 'replied',
    priority: 'normal',
    category: 'feedback',
    isRead: true
  }
];

const mockConversations: Conversation[] = [
  {
    id: '1',
    participants: [
      { id: 'coach1', name: 'Coach Sarah Wilson', role: 'Coach' },
      { id: 'parent1', name: 'Michael Thompson', role: 'Parent' }
    ],
    lastMessage: 'Thank you for the detailed update on Alex\'s progress.',
    lastMessageTime: '2024-01-15T15:20:00Z',
    unreadCount: 1,
    category: 'Progress Updates'
  },
  {
    id: '2',
    participants: [
      { id: 'coach1', name: 'Coach Sarah Wilson', role: 'Coach' },
      { id: 'coach2', name: 'Coach Mike Johnson', role: 'Coach' }
    ],
    lastMessage: 'Let\'s schedule a meeting for tomorrow afternoon.',
    lastMessageTime: '2024-01-15T11:30:00Z',
    unreadCount: 0,
    category: 'Coach Collaboration'
  },
  {
    id: '3',
    participants: [
      { id: 'admin1', name: 'Academy Admin', role: 'Admin' },
      { id: 'all_coaches', name: 'All Coaches', role: 'Group' }
    ],
    lastMessage: 'Please confirm your attendance for the equipment training.',
    lastMessageTime: '2024-01-15T09:30:00Z',
    unreadCount: 2,
    category: 'Announcements'
  }
];

const templates = [
  {
    id: 'progress_update',
    name: 'Weekly Progress Update',
    subject: 'Weekly Progress Update for {player_name}',
    content: 'Hi {parent_name},\n\nI hope you\'re doing well. I wanted to share {player_name}\'s progress from this week:\n\n• Shot Distance: {avg_distance}m (improvement of {distance_improvement})\n• Consistency Score: {consistency_score}%\n• Areas of improvement: {improvement_areas}\n• Recommendations: {recommendations}\n\nPlease let me know if you have any questions!\n\nBest regards,\n{coach_name}'
  },
  {
    id: 'session_reminder',
    name: 'Session Reminder',
    subject: 'Training Session Reminder - {date}',
    content: 'Hi {player_name},\n\nThis is a friendly reminder about your upcoming training session:\n\n• Date: {date}\n• Time: {time}\n• Location: {location}\n• Focus: {session_focus}\n\nPlease arrive 10 minutes early and bring your equipment.\n\nSee you soon!\n{coach_name}'
  },
  {
    id: 'achievement',
    name: 'Achievement Celebration',
    subject: 'Congratulations on {achievement}!',
    content: 'Hi {player_name},\n\nCongratulations on achieving {achievement}! This is a fantastic milestone and reflects your hard work and dedication.\n\n• Previous best: {previous_record}\n• New achievement: {new_record}\n• Improvement: {improvement_percentage}\n\nKeep up the excellent work!\n\nBest regards,\n{coach_name}'
  }
];

export const CommunicationHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newMessage, setNewMessage] = useState({
    to: '',
    subject: '',
    content: '',
    priority: 'normal' as const
  });

  const filteredMessages = mockMessages.filter(message =>
    message.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.fromName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = mockMessages.filter(msg => !msg.isRead).length;

  const handleSendMessage = () => {
    console.log('Sending message:', newMessage);
    setIsComposeOpen(false);
    setNewMessage({ to: '', subject: '', content: '', priority: 'normal' });
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setNewMessage(prev => ({
        ...prev,
        subject: template.subject,
        content: template.content
      }));
    }
  };

  const getPriorityColor = (priority: Message['priority']) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'normal': return 'secondary';
      case 'low': return 'outline';
    }
  };

  const getStatusIcon = (status: Message['status']) => {
    switch (status) {
      case 'sent': return Clock;
      case 'delivered': return CheckCircle;
      case 'read': return CheckCircle;
      case 'replied': return Reply;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return format(date, 'HH:mm');
    } else if (diffInHours < 168) { // Less than a week
      return format(date, 'EEE HH:mm');
    } else {
      return format(date, 'MMM dd');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Communication Hub</h2>
          <p className="text-muted-foreground">
            Manage messages and communicate with players, parents, and coaches
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" className="flex items-center space-x-2">
            <Bell className="w-4 h-4" />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                {unreadCount}
              </Badge>
            )}
          </Button>
          <Button onClick={() => setIsComposeOpen(true)} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Compose</span>
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="messages" className="flex items-center space-x-2">
            <MessageSquare className="w-4 h-4" />
            <span>Messages</span>
          </TabsTrigger>
          <TabsTrigger value="conversations" className="flex items-center space-x-2">
            <Users className="w-4 h-4" />
            <span>Conversations</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center space-x-2">
            <Star className="w-4 h-4" />
            <span>Templates</span>
          </TabsTrigger>
          <TabsTrigger value="announcements" className="flex items-center space-x-2">
            <Bell className="w-4 h-4" />
            <span>Announcements</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="space-y-4">
          {/* Search and Filters */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button variant="outline" className="flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </Button>
          </div>

          {/* Messages List */}
          <div className="space-y-3">
            {filteredMessages.map((message) => {
              const StatusIcon = getStatusIcon(message.status);
              return (
                <Card 
                  key={message.id} 
                  className={`cursor-pointer transition-colors hover:bg-gray-50 ${!message.isRead ? 'border-blue-200 bg-blue-50/30' : ''}`}
                  onClick={() => setSelectedMessage(message)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={message.fromAvatar} />
                          <AvatarFallback>
                            {message.fromName.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <h3 className={`font-medium ${!message.isRead ? 'font-semibold' : ''}`}>
                                {message.fromName}
                              </h3>
                              <Badge variant="outline" className="text-xs">
                                {message.fromRole}
                              </Badge>
                              <Badge variant={getPriorityColor(message.priority)} className="text-xs">
                                {message.priority}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <StatusIcon className="w-4 h-4" />
                              <span>{formatTime(message.timestamp)}</span>
                            </div>
                          </div>
                          
                          <p className={`text-sm ${!message.isRead ? 'font-medium' : 'text-muted-foreground'} mt-1`}>
                            {message.subject}
                          </p>
                          
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {message.content}
                          </p>
                          
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="flex items-center space-x-1 mt-2">
                              <Paperclip className="w-4 h-4 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {message.attachments.length} attachment(s)
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="conversations">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>Active Conversations</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {mockConversations.map((conversation) => (
                      <div key={conversation.id} className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="flex -space-x-2">
                              {conversation.participants.slice(0, 2).map((participant, _index) => (
                                <Avatar key={participant.id} className="h-8 w-8 border-2 border-white">
                                  <AvatarFallback className="text-xs">
                                    {participant.name.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                              ))}
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {conversation.participants.map(p => p.name.split(' ')[0]).join(', ')}
                              </p>
                              <p className="text-xs text-muted-foreground">{conversation.category}</p>
                            </div>
                          </div>
                          {conversation.unreadCount > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {conversation.unreadCount}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                          {conversation.lastMessage}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTime(conversation.lastMessageTime)}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Conversation</CardTitle>
                <CardDescription>Select a conversation to view messages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mb-4" />
                  <p>Select a conversation to start messaging</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <CardTitle>{template.name}</CardTitle>
                  <CardDescription>Template for automated communications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Subject:</Label>
                    <p className="text-sm text-muted-foreground mt-1">{template.subject}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Content Preview:</Label>
                    <div className="bg-gray-50 p-3 rounded-md mt-1">
                      <p className="text-sm text-muted-foreground line-clamp-6">
                        {template.content}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => handleTemplateSelect(template.id)}>
                      Use Template
                    </Button>
                    <Button size="sm" variant="outline">
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="announcements">
          <Card>
            <CardHeader>
              <CardTitle>Academy Announcements</CardTitle>
              <CardDescription>
                Send announcements to all coaches, players, or parents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Audience</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select audience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Everyone</SelectItem>
                        <SelectItem value="coaches">All Coaches</SelectItem>
                        <SelectItem value="players">All Players</SelectItem>
                        <SelectItem value="parents">All Parents</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input placeholder="Enter announcement subject" />
                </div>
                
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea 
                    placeholder="Type your announcement message here..."
                    rows={6}
                  />
                </div>
                
                <div className="flex space-x-3">
                  <Button className="flex items-center space-x-2">
                    <Send className="w-4 h-4" />
                    <span>Send Announcement</span>
                  </Button>
                  <Button variant="outline">
                    Save Draft
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Compose Message Dialog */}
      <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Compose Message</DialogTitle>
            <DialogDescription>
              Send a message to players, parents, or fellow coaches
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>To</Label>
                <Select value={newMessage.to} onValueChange={(value) => setNewMessage(prev => ({ ...prev, to: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent1">Michael Thompson (Parent)</SelectItem>
                    <SelectItem value="parent2">Lisa Chen (Parent)</SelectItem>
                    <SelectItem value="coach2">Coach Mike Johnson</SelectItem>
                    <SelectItem value="player1">Alex Thompson</SelectItem>
                    <SelectItem value="player2">Sarah Chen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newMessage.priority} onValueChange={(value) => setNewMessage(prev => ({ ...prev, priority: value as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Template (Optional)</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input 
                placeholder="Enter message subject"
                value={newMessage.subject}
                onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea 
                placeholder="Type your message here..."
                rows={8}
                value={newMessage.content}
                onChange={(e) => setNewMessage(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsComposeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendMessage} className="flex items-center space-x-2">
              <Send className="w-4 h-4" />
              <span>Send Message</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedMessage?.subject}</DialogTitle>
            <DialogDescription>
              From {selectedMessage?.fromName} • {selectedMessage && formatTime(selectedMessage.timestamp)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={selectedMessage?.fromAvatar} />
                <AvatarFallback>
                  {selectedMessage?.fromName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{selectedMessage?.fromName}</p>
                <p className="text-sm text-muted-foreground">
                  To: {selectedMessage?.toName}
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="whitespace-pre-wrap">{selectedMessage?.content}</p>
            </div>
            
            {selectedMessage?.attachments && selectedMessage.attachments.length > 0 && (
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div className="space-y-1">
                  {selectedMessage.attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                      <Paperclip className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{attachment}</span>
                      <Button size="sm" variant="outline">
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedMessage(null)}>
              Close
            </Button>
            <Button variant="outline" className="flex items-center space-x-2">
              <Forward className="w-4 h-4" />
              <span>Forward</span>
            </Button>
            <Button className="flex items-center space-x-2">
              <Reply className="w-4 h-4" />
              <span>Reply</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};